// EventVolunteersDialog.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Box,
  IconButton,
  TextField,
  InputAdornment,
  Chip
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  Phone as PhoneIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase';

const EventVolunteersDialog = ({ open, onClose, eventId, eventTitle }) => {
  const [volunteers, setVolunteers] = useState([]);
  const [filteredVolunteers, setFilteredVolunteers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (open && eventId) {
      fetchVolunteers();
    }
  }, [open, eventId]);

  useEffect(() => {
    // Filtrar voluntarios según el término de búsqueda
    if (searchTerm) {
      const filtered = volunteers.filter(volunteer => 
        volunteer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        volunteer.phone?.includes(searchTerm)
      );
      setFilteredVolunteers(filtered);
    } else {
      setFilteredVolunteers(volunteers);
    }
  }, [searchTerm, volunteers]);

  const fetchVolunteers = async () => {
    setLoading(true);
    try {
      // Primero obtener el evento para acceder a la lista de voluntarios
      const eventDoc = await getDoc(doc(db, 'events', eventId));
      
      if (eventDoc.exists()) {
        const eventData = eventDoc.data();
        const volunteerIds = eventData.voluntarios || []; // Cambié de 'volunteers' a 'voluntarios'
        
        if (volunteerIds.length > 0) {
          // Obtener información de cada voluntario
          const volunteerPromises = volunteerIds.map(async (volunteerId) => {
            const userDoc = await getDoc(doc(db, 'users', volunteerId));
            if (userDoc.exists()) {
              const userData = userDoc.data();
              return {
                id: volunteerId,
                name: `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || 'Sin nombre',
                phone: userData.phone || 'No especificado',
                email: userData.email
              };
            }
            return null;
          });
          
          const volunteerData = await Promise.all(volunteerPromises);
          const validVolunteers = volunteerData.filter(v => v !== null);
          setVolunteers(validVolunteers);
          setFilteredVolunteers(validVolunteers);
        } else {
          setVolunteers([]);
          setFilteredVolunteers([]);
        }
      }
    } catch (error) {
      console.error('Error al cargar voluntarios:', error);
      setVolunteers([]);
      setFilteredVolunteers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Voluntarios - {eventTitle}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        {volunteers.length > 0 && (
          <Box mb={2}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Buscar por nombre o teléfono..."
              value={searchTerm}
              onChange={handleSearchChange}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        )}
        
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        ) : filteredVolunteers.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <Typography variant="body1" color="textSecondary">
              {searchTerm ? 'No se encontraron voluntarios con ese criterio' : 'No hay voluntarios registrados para este evento'}
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <PersonIcon fontSize="small" sx={{ mr: 1 }} />
                      Nombre
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <PhoneIcon fontSize="small" sx={{ mr: 1 }} />
                      Teléfono
                    </Box>
                  </TableCell>
                  <TableCell>Email</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredVolunteers.map((volunteer) => (
                  <TableRow key={volunteer.id} hover>
                    <TableCell>{volunteer.name}</TableCell>
                    <TableCell>
                      {volunteer.phone !== 'No especificado' ? (
                        <Chip
                          label={volunteer.phone}
                          size="small"
                          icon={<PhoneIcon />}
                          variant="outlined"
                          clickable
                          onClick={() => window.open(`tel:${volunteer.phone}`, '_blank')}
                        />
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          No especificado
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="textSecondary">
                        {volunteer.email}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        
        <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="body2" color="textSecondary">
            Total de voluntarios: {volunteers.length}
          </Typography>
          {volunteers.length > 0 && (
            <Typography variant="body2" color="textSecondary">
              Mostrando: {filteredVolunteers.length}
            </Typography>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EventVolunteersDialog;